import { Ref } from 'react';
import { Button, Input, Link, TextContent } from '@amzn/awsui-components-core';
import { BaseKeyDetail } from '@amzn/awsui-components-core/internal/events';
import { PublisherReviewStatus, WorkflowState } from '@amzn/awscat-pattern-library-service-model';

import { CommentCardItem } from './CommentCardViewModel';
import { useCommentCardViewModel } from './CommentCardViewModel';
import { ReplyView } from './ReplyView';
import styles from './CommentCard.module.scss';

export interface CommentCardProps {
  readonly comment: CommentCardItem;
  readonly draftState: WorkflowState;
  readonly reviewStatus: PublisherReviewStatus;
  readonly isSelected: boolean;
  readonly isResolved: boolean;
  readonly onClick: () => void;
  readonly ref?: Ref<HTMLDivElement>;
}

export function CommentCard({
  comment,
  draftState,
  reviewStatus,
  isSelected,
  isResolved,
  onClick,
  ref,
}: CommentCardProps) {
  const vm = useCommentCardViewModel(comment, isResolved, draftState, reviewStatus);

  const cardClassName = [
    styles.commentCard,
    isSelected ? (vm.activeStyleVariant === 'ai' ? styles.activeAi : styles.active) : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={ref}
      className={cardClassName}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      role={isSelected ? undefined : 'button'}
      tabIndex={isSelected ? undefined : 0}
    >
      {isSelected && (
        <div className={styles.actionsBar} onClick={(e) => e.stopPropagation()}>
          {isResolved ? (
            <Button variant="inline-link" disabled={vm.isPending} onClick={vm.reopen}>
              ↩ Reopen
            </Button>
          ) : (
            <>
              {vm.isCanResolve && (
                <Button variant="inline-link" disabled={vm.isPending} onClick={vm.resolve}>
                  {vm.isPending ? '...' : vm.resolveLabel}
                </Button>
              )}

              {vm.isShowIgnoreAction && (
                <Button variant="inline-link" disabled={vm.isPending} onClick={vm.markIgnored}>
                  ✕ Ignored
                </Button>
              )}

              {vm.isCanEdit && (
                <Button variant="inline-link" disabled={vm.isPending} onClick={vm.startEdit}>
                  ✎ Edit
                </Button>
              )}

              {vm.isCanDelete && (
                <Button variant="inline-link" disabled={vm.isPending} onClick={vm.deleteComment}>
                  {vm.deleteLabel}
                </Button>
              )}
            </>
          )}
        </div>
      )}

      <div className={styles.cardBody}>
        {comment.quotedText && (
          <div className={styles.quotedText}>
            <TextContent>&ldquo;{comment.quotedText}&rdquo;</TextContent>
          </div>
        )}

        <div className={styles.commentMeta}>
          <TextContent>
            <span className={styles.author}>{comment.scannerDisplayName}</span>
            <span className={styles.dot}>&middot;</span>
            <span className={styles.time}>{comment.relativeTime}</span>
          </TextContent>
        </div>

        {isSelected && vm.isEditing ? (
          <div className={styles.editInput} onClick={(e) => e.stopPropagation()}>
            <Input
              value={vm.editText}
              onChange={({ detail }) => vm.setEditText(detail.value)}
              onKeyDown={(e: CustomEvent<BaseKeyDetail>) => {
                if (e.detail.key === 'Enter') vm.saveEdit();
                if (e.detail.key === 'Escape') vm.cancelEdit();
              }}
            />
            {vm.editError && (
              <div className={styles.editError}>
                <TextContent>{vm.editError}</TextContent>
              </div>
            )}
            <div className={styles.editActions}>
              <Button variant="inline-link" disabled={vm.isPending} onClick={vm.saveEdit}>
                Save
              </Button>
              <Button variant="inline-link" onClick={vm.cancelEdit}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className={isSelected ? styles.commentBody : styles.commentBodyTruncated}>
            <TextContent>{comment.commentBody}</TextContent>
          </div>
        )}

        {comment.sourceUrl && isSelected && (
          <div className={styles.sourceLink}>
            <Link href={comment.sourceUrl} external fontSize="body-s">
              View source
            </Link>
          </div>
        )}

        <ReplyView threadId={comment.threadId} replies={comment.replies} isExpanded={isSelected} />
      </div>
    </div>
  );
}
